/**
 * fleetReportBuilder — 22/07/2026 (retour user prod).
 * « Vue des camions et de leurs contrats — rapport à produire par Forni,
 * trimestriel et fin d'année, de leurs usages et de leur rentabilité. »
 *
 * Données accumulées par tickVehicles (q* = trimestre, y* = exercice,
 * remises à zéro par coreSlice à chaque clôture) + `contractTypes`
 * (typologie des contrats acceptés avec chaque camion, contractSlice).
 * Rapports réservés au joueur ayant recruté Rémi Forni
 * (role-christophe-faure, Responsable d'exploitation).
 */

const fmt = (n) => Math.round(n || 0).toLocaleString('fr-FR');

/** Lignes par camion pour une période ('q' ou 'y'), triées par résultat. */
export function buildFleetRows(vehicles, activeContracts, prefix) {
  const contractByVehicle = {};
  (activeContracts || []).forEach(c => {
    if (c.vehicleId) contractByVehicle[c.vehicleId] = c;
  });
  return (vehicles || [])
    .filter(v => v.status !== 'sold')
    .map(v => {
      const revenue = Math.round(v[`${prefix}Revenue`] || 0);
      const fuelCost = Math.round(v[`${prefix}FuelCost`] || 0);
      const fixedCost = Math.round(v[`${prefix}FixedCost`] || 0);
      const profit = revenue - fuelCost - fixedCost;
      return {
        id: v.id,
        label: `${v.brand || ''} ${v.model || ''}`.trim() || 'Camion',
        isLeased: !!v.isLeased,
        wear: Math.round(v.wear || 0),
        activeDays: v[`${prefix}ActiveDays`] || 0,
        downDays: v[`${prefix}DownDays`] || 0,
        revenue,
        fuelCost,
        fixedCost,
        profit,
        marginPct: revenue > 0 ? Math.round((profit / revenue) * 100) : null,
        contractTypes: v.contractTypes || {},
        currentContract: contractByVehicle[v.id]?.name || null,
      };
    })
    .sort((a, b) => b.profit - a.profit);
}

/** Rapport annuel (bloc Conseil). Null si flotte vide. */
export function buildFleetYearReport(vehicles, activeContracts) {
  const rows = buildFleetRows(vehicles, activeContracts, 'y');
  if (rows.length === 0) return null;
  const totals = rows.reduce((t, r) => ({
    revenue: t.revenue + r.revenue,
    fuelCost: t.fuelCost + r.fuelCost,
    fixedCost: t.fixedCost + r.fixedCost,
    profit: t.profit + r.profit,
  }), { revenue: 0, fuelCost: 0, fixedCost: 0, profit: 0 });
  return { rows, totals };
}

const typesLabel = (contractTypes) =>
  Object.entries(contractTypes || {})
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => `${k} ×${n}`)
    .join(', ');

/** Message trimestriel de Rémi Forni (payload pushCustomMessage). */
export function buildFleetQuarterMessage(vehicles, activeContracts, meta = {}) {
  const rows = buildFleetRows(vehicles, activeContracts, 'q');
  if (rows.length === 0) return null;
  const lines = rows.map(r => {
    const types = typesLabel(r.contractTypes);
    return `• **${r.label}**${r.isLeased ? ' (leasing)' : ''} — CA ${fmt(r.revenue)} €, `
      + `${r.activeDays} j actifs${r.downDays > 0 ? `, ${r.downDays} j immobilisé` : ''}, `
      + `coûts ${fmt(r.fuelCost + r.fixedCost)} € → résultat ${r.profit >= 0 ? '+' : ''}${fmt(r.profit)} €`
      + `${r.marginPct !== null ? ` (marge ${r.marginPct} %)` : ''}`
      + `${r.currentContract ? `\n   Contrat en cours : ${r.currentContract}` : ''}`
      + `${types ? `\n   Contrats pris avec ce camion : ${types}` : ''}`;
  });
  const worst = rows[rows.length - 1];
  const losing = rows.filter(r => r.profit < 0);
  let advice;
  if (losing.length > 0) {
    advice = `⚠️ ${losing.length} camion${losing.length > 1 ? 's' : ''} en perte sur le trimestre `
      + `(dont ${worst.label} : ${fmt(worst.profit)} €). Pistes : réaffecter sur des contrats mieux payés, `
      + `réduire l'immobilisation, ou envisager la revente si ça persiste.`;
  } else {
    advice = `Toute la flotte est rentable ce trimestre. Je garde un œil sur l'usure (${rows.map(r => `${r.label} ${r.wear} %`).join(', ')}).`;
  }
  return {
    senderType: 'support',
    senderName: 'Rémi Forni (Resp. exploitation)',
    senderRole: 'role-christophe-faure',
    kind: 'info',
    tone: losing.length > 0 ? 'warning' : 'advice',
    subject: `Rapport flotte T${meta.quarter || '?'} — usage & rentabilité par camion`,
    body: `Patron,\n\nVoici mon point trimestriel camion par camion (CA attribué, jours d'usage, coûts carburant + entretien/assurance/financement) :\n\n${lines.join('\n\n')}\n\n${advice}\n\nÀ vos ordres,\n— Rémi Forni`,
    ttlDays: 21,
  };
}

/**
 * Activité quotidienne des chauffeurs (fix « 0 trajets », 22/07/2026).
 * Mutation in-place de `drivers` (pattern annualReportBuilder) : +1 trajet
 * et CA attribué pour chaque jour ouvré servi, +1 jour manqué si le
 * contrat a raté sa livraison à cause du chauffeur/camion.
 */
export function applyDriverDailyActivity(drivers, activeContracts, missMap) {
  const served = {};
  const missed = new Set();
  (activeContracts || []).forEach(c => {
    if (!c.vehicleId || !c.driverId) return;
    const m = (missMap || {})[c.instanceId];
    if (m === true || m === 'stale_driver') {
      missed.add(c.driverId);
      return;
    }
    // Revenu plein ou toléré/pénalisé : le chauffeur a roulé.
    served[c.driverId] = (served[c.driverId] || 0) + (c.dailyRevenue || 0);
  });
  for (let i = 0; i < drivers.length; i++) {
    const d = drivers[i];
    if (served[d.id] !== undefined) {
      drivers[i] = {
        ...d,
        totalTrips: (d.totalTrips || 0) + 1,
        yearTrips: (d.yearTrips || 0) + 1,
        yearRevenue: (d.yearRevenue || 0) + Math.round(served[d.id]),
      };
    } else if (missed.has(d.id)) {
      drivers[i] = { ...d, yearMissedDays: (d.yearMissedDays || 0) + 1 };
    }
  }
}
